/**
 * A small, dismissible hint shown ONLY on phones / small tablets
 * (xs and sm breakpoints — hidden from md+ entirely) for pages that
 * are dense with analytics tables and charts.  Tells the analyst they
 * can keep going on mobile but the full view lives on desktop.
 *
 * Dismissal is persisted in localStorage so it shows at most once per
 * device until the user clears storage.
 *
 * Opted into per-layout via SiteLayout's `showDesktopHint` prop —
 * deliberately NOT shown on simple form-only pages (Profile, change
 * password) where there's nothing better on desktop.
 */

import CloseIcon from '@mui/icons-material/Close';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const GOLD = '#bb8c3f';
const INK = '#0f0a07';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const STORAGE_KEY = 'hy.desktopHintDismissed';

export function DesktopRecommendationHint() {
  // Default to "dismissed" while we read localStorage to avoid a
  // flash of the banner on every navigation.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      setDismissed(stored === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* private browsing — fine, just keep hiding for this session */
    }
  };

  if (dismissed) return null;

  return (
    <Box
      role="note"
      sx={{
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        gap: 1,
        mx: { xs: 2, sm: 3 },
        mt: { xs: 1.5, sm: 2 },
        px: 1.5,
        py: 1,
        borderRadius: 1.25,
        bgcolor: 'rgba(187, 140, 63, 0.08)',
        border: '1px solid rgba(187, 140, 63, 0.28)',
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          bgcolor: 'rgba(187, 140, 63, 0.16)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <DesktopMacOutlinedIcon sx={{ fontSize: 15 }} />
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontFamily: FONT_INTER,
          fontSize: 12,
          lineHeight: 1.4,
          color: INK,
        }}
      >
        For the full analytics view, open on a desktop.
      </Typography>
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label="Dismiss desktop recommendation"
        sx={{
          color: 'rgba(15, 10, 7, 0.5)',
          p: 0.4,
          '&:hover': { color: GOLD, bgcolor: 'rgba(187, 140, 63, 0.10)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}
